import { AuthQueryProvider } from "@daveyplate/better-auth-tanstack";
import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	HeadContent,
	Link,
	Outlet,
	Scripts,
} from "@tanstack/react-router";
import { CircleCheck, CircleX, Info, TriangleAlert } from "lucide-react";
import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "sonner";

import { LazyMotionProvider } from "@/components/LazyMotionProvider";
import { PostHogAnalyticsProvider } from "@/components/PostHogAnalytics";
import { ProDialogProvider } from "@/components/ProDialog";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { Button } from "@/components/ui/button";
import { useSession } from "@/hooks/auth-hooks";
import { SubscriptionProvider } from "@/hooks/use-subscription";
import { useThemeShortcut } from "@/hooks/use-theme-shortcut";
import {
	captureAffiliateAttributionFromLocation,
	getStoredAffiliateAttribution,
} from "@/lib/affiliate";
import { ThemeProvider } from "@/utils/themeProvider";

const shouldLoadDevTools = import.meta.env.VITE_ENABLE_DEVTOOLS === "true";

const DevTools = shouldLoadDevTools
	? lazy(() => import("@/components/DevTools"))
	: null;

import appCss from "../styles.css?url";

const gtmId = import.meta.env.PROD ? import.meta.env.VITE_GTM_ID : undefined;

type DataLayerWindow = Window & {
	dataLayer?: Record<string, unknown>[];
};

interface MyRouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1, viewport-fit=cover",
			},
			{
				title: "AgentReacher | Connect Your AI to Social Media",
			},
		],
		links: [
			{
				rel: "preload",
				href: "/fonts/Geist-Variable.woff2",
				as: "font",
				type: "font/woff2",
				crossOrigin: "anonymous",
			},
			{
				rel: "preload",
				href: "/fonts/GeistMono-Variable.woff2",
				as: "font",
				type: "font/woff2",
				crossOrigin: "anonymous",
			},
			{
				rel: "icon",
				type: "image/svg+xml",
				href: "/favicon.svg",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/favicon-32.png",
			},
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/apple-touch-icon.png",
			},
			{
				rel: "manifest",
				href: "/manifest.json",
			},
		],
	}),

	shellComponent: RootDocument,
	notFoundComponent: NotFound,
});

function NotFound() {
	const { session } = useSession();
	const homeTo = session ? "/dashboard" : "/";

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />

			<main className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						<div className="flex min-h-[60vh] items-center justify-center rounded-md bg-white dark:bg-black px-4 py-20 sm:py-28">
							<div className="flex flex-col items-center text-center">
								<h1 className="text-6xl font-extrabold tracking-[-0.04em] text-foreground sm:text-7xl">
									404
								</h1>
								<h2 className="mt-8 text-2xl font-semibold tracking-[-0.02em] sm:text-3xl">
									Page Not Found
								</h2>
								<p className="mt-4 max-w-md text-base text-muted-foreground">
									The page you are looking for doesn't exist or has been moved.
								</p>
								<Button asChild size="lg" className="mt-10">
									<Link to={homeTo}>Return to Home</Link>
								</Button>
							</div>
						</div>
					</div>
				</div>
			</main>

			<PublicFooter />
		</div>
	);
}

function ThemeShortcut() {
	useThemeShortcut();
	return null;
}

function GoogleTagManager() {
	useEffect(() => {
		if (!gtmId || document.getElementById("google-tag-manager")) return;

		const dataLayerWindow = window as DataLayerWindow;
		dataLayerWindow.dataLayer = dataLayerWindow.dataLayer || [];
		dataLayerWindow.dataLayer.push({
			"gtm.start": Date.now(),
			event: "gtm.js",
		});

		const script = document.createElement("script");
		script.id = "google-tag-manager";
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;

		const firstScript = document.getElementsByTagName("script")[0];
		firstScript.parentNode?.insertBefore(script, firstScript);
	}, []);

	return null;
}

function AffiliateAttributionCapture() {
	useEffect(() => {
		captureAffiliateAttributionFromLocation();
	}, []);

	return null;
}

function AffiliateReferralClaim() {
	const { user, isPending } = useSession();

	useEffect(() => {
		if (isPending || !user?.id) return;

		const attribution = getStoredAffiliateAttribution();
		if (!attribution) return;

		const claimKey = `agentreacher:affiliate-claim:${user.id}:${attribution.code}`;
		if (window.localStorage.getItem(claimKey)) return;

		window.localStorage.setItem(claimKey, "1");
		fetch("/api/affiliate/claim", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ affiliateCode: attribution.code }),
		})
			.then((response) => {
				if (!response.ok) window.localStorage.removeItem(claimKey);
			})
			.catch(() => {
				window.localStorage.removeItem(claimKey);
			});
	}, [isPending, user?.id]);

	return null;
}

function RootDocument() {
	useEffect(() => {
		if (import.meta.env.DEV) {
			void import("react-grab").then(({ registerPlugin }) => {
				registerPlugin({
					name: "hide-ui",
					theme: { toolbar: { enabled: false } },
				});
			});
			void import("@react-grab/mcp/client");
		}
	}, []);

	return (
		<html lang="en" suppressHydrationWarning>
			<head>
				<HeadContent />
				<link rel="stylesheet" href={appCss} suppressHydrationWarning />
			</head>
			<body>
				{gtmId ? (
					<noscript>
						<iframe
							title="Google Tag Manager"
							src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
							height="0"
							width="0"
							style={{ display: "none", visibility: "hidden" }}
						/>
					</noscript>
				) : null}
				<GoogleTagManager />
				<ThemeProvider
					attribute="class"
					defaultTheme="dark"
					disableTransitionOnChange
					enableSystem
				>
					<ThemeShortcut />
					<AffiliateAttributionCapture />
					<LazyMotionProvider>
						<AuthQueryProvider>
							<AffiliateReferralClaim />
							<PostHogAnalyticsProvider>
								<SubscriptionProvider>
									<ProDialogProvider>
										<Outlet />
										{DevTools ? (
											<Suspense fallback={null}>
												<DevTools />
											</Suspense>
										) : null}
										<Toaster
											position="top-center"
											icons={{
												success: <CircleCheck className="text-emerald-500" />,
												error: <CircleX className="text-red-500" />,
												info: <Info className="text-blue-500" />,
												warning: <TriangleAlert className="text-amber-500" />,
											}}
											toastOptions={{
												style: {
													fontFamily:
														"'Geist', 'Geist Fallback', ui-sans-serif, system-ui, sans-serif",
													gap: "16px",
												},
												classNames: {
													toast:
														"bg-background border-2 border-stone-300/30 dark:border-stone-700/30 shadow-lg",
													title: "text-foreground font-semibold",
													description: "text-muted-foreground",
													success:
														"!bg-emerald-50 dark:!bg-emerald-950 !border-emerald-200 dark:!border-emerald-800 [&_svg]:!text-emerald-500 [&_[data-icon]]:!text-emerald-500 [&_[data-title]]:!text-emerald-900 dark:[&_[data-title]]:!text-emerald-100 [&_[data-description]]:!text-emerald-700 dark:[&_[data-description]]:!text-emerald-300",
													error:
														"!bg-red-50 dark:!bg-red-950 !border-red-200 dark:!border-red-800 [&>svg]:!text-red-500 [&_[data-title]]:!text-red-900 dark:[&_[data-title]]:!text-red-100 [&_[data-description]]:!text-red-700 dark:[&_[data-description]]:!text-red-300",
													info: "!bg-blue-50 dark:!bg-blue-950 !border-blue-200 dark:!border-blue-800 [&>svg]:!text-blue-500 [&_[data-title]]:!text-blue-900 dark:[&_[data-title]]:!text-blue-100 [&_[data-description]]:!text-blue-700 dark:[&_[data-description]]:!text-blue-300",
													warning:
														"!bg-amber-50 dark:!bg-amber-950 !border-amber-200 dark:!border-amber-800 [&>svg]:!text-amber-500 [&_[data-title]]:!text-amber-900 dark:[&_[data-title]]:!text-amber-100 [&_[data-description]]:!text-amber-700 dark:[&_[data-description]]:!text-amber-300",
												},
											}}
										/>
									</ProDialogProvider>
								</SubscriptionProvider>
							</PostHogAnalyticsProvider>
						</AuthQueryProvider>
					</LazyMotionProvider>
				</ThemeProvider>
				<Scripts />
			</body>
		</html>
	);
}
